import type { MarkdownDocument } from '@markbird/shared';
import { createDocument, setLastOpenDocId } from './library';

/**
 * "Share → markbird" from the operating system (Android, and desktop Chrome/Edge
 * for installed PWAs).
 *
 * The manifest's share target POSTs the shared title/text/url/files to
 * /share-target, and public/share-target-sw.js — imported into the service
 * worker — answers it without the request ever reaching the network: it stores
 * the payload in Cache Storage and redirects to /?shared=1. This module picks
 * the payload up at boot and turns it into documents.
 *
 * POST through the service worker rather than the simpler GET share target on
 * purpose. GET puts the shared text in the query string, which is sent to the
 * host and written to its access logs — the same reason the Claude handoff uses
 * a URL fragment. Nothing shared into markbird should leave the device for it.
 */

export const SHARE_CACHE = 'markbird-share-target';

/** What the service worker stores for one share. */
export interface SharedPayload {
  title?: string;
  text?: string;
  url?: string;
  files?: { name: string; content: string }[];
}

/**
 * The document a share becomes. Apps fill the three fields inconsistently — a
 * browser may put the page URL in `text`, a notes app the whole note in `text`
 * and nothing in `title` — so the URL is appended only when it is not already in
 * the text, and a missing title is left for markbird to derive from the content.
 */
export function sharedToDocument(
  payload: SharedPayload,
): { title?: string; content: string } | null {
  const title = payload.title?.trim() || undefined;
  const text = payload.text?.trim() ?? '';
  const url = payload.url?.trim() ?? '';
  const parts: string[] = [];
  if (text) parts.push(text);
  if (url && !text.includes(url)) parts.push(`<${url}>`);
  if (parts.length === 0) return title ? { title, content: `# ${title}\n` } : null;
  return { title, content: parts.join('\n\n') + '\n' };
}

let pending = false;

/**
 * Note a share launch and strip the marker from the address bar, before React
 * mounts — so a reload does not import the same share twice.
 */
export function captureSharedLaunch(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('shared') !== '1') return;
  pending = true;
  window.history.replaceState(null, '', '/');
}

/**
 * Turn stored shares into documents, oldest first, and clear them. Returns the
 * last document created, which the editor opens, or null for an ordinary launch.
 */
export async function consumeSharedDocs(): Promise<MarkdownDocument | null> {
  if (!pending || typeof caches === 'undefined') return null;
  pending = false;

  const cache = await caches.open(SHARE_CACHE);
  const requests = [...(await cache.keys())].sort((a, b) => a.url.localeCompare(b.url));
  let last: MarkdownDocument | null = null;

  for (const request of requests) {
    const response = await cache.match(request);
    await cache.delete(request);
    if (!response) continue;
    let payload: SharedPayload;
    try {
      payload = (await response.json()) as SharedPayload;
    } catch {
      continue;
    }
    for (const file of payload.files ?? []) {
      const title = file.name.replace(/\.(md|markdown|mdown|mkd|txt)$/i, '') || undefined;
      last = await createDocument({ title, content: file.content });
    }
    const fromFields = sharedToDocument(payload);
    if (fromFields) last = await createDocument(fromFields);
  }

  if (last) await setLastOpenDocId(last.id);
  return last;
}
